/**
 * Peak-tile identity helpers.
 *
 * The Peak tiles were renamed:
 *   PeakSmall       → SmallPeak
 *   PeakLarge       → LargeCenterPeak
 *   PeakLargeOffset → LargeUpperPeak
 *
 * Existing saved designs still contain the old IDs in their persisted shapes
 * JSON, so runtime detection code must recognise BOTH old and new names.
 * These helpers centralise that check.
 */

/** All peak-tile shape IDs (old legacy + current canonical), including Freeform and Flipped variants. */
const PEAK_IDS = new Set([
  // Current canonical
  'SmallPeak', 'LargeCenterPeak', 'LargeUpperPeak', 'LargeUpperPeakFlipped',
  'SmallPeakFreeform', 'LargeCenterPeakFreeform', 'LargeUpperPeakFreeform', 'LargeUpperPeakFlippedFreeform',
  // 90° rotated picker variants
  'SmallPeak90', 'LargeCenterPeak90', 'LargeUpperPeak90',
  'SmallPeakFreeform90', 'LargeCenterPeakFreeform90', 'LargeUpperPeakFreeform90',
  // Large Upper Peak four-way rotation set (180° replaces the old "Flipped" variant)
  'LargeUpperPeak180', 'LargeUpperPeak270',
  'LargeUpperPeakFreeform180', 'LargeUpperPeakFreeform270',
  // Legacy (pre-rename, still stored in older designs)
  'PeakSmall', 'PeakLarge', 'PeakLargeOffset', 'PeakLargeOffsetFlipped',
  'PeakSmallFreeform', 'PeakLargeFreeform', 'PeakLargeOffsetFreeform', 'PeakLargeOffsetFlippedFreeform',
])

/** IDs that represent the upper/offset peak variant (the fold sits near the top, not centered). */
const UPPER_PEAK_IDS = new Set([
  'LargeUpperPeak', 'LargeUpperPeakFlipped', 'LargeUpperPeakFreeform', 'LargeUpperPeakFlippedFreeform',
  'LargeUpperPeak90', 'LargeUpperPeakFreeform90',
  'LargeUpperPeak180', 'LargeUpperPeak270', 'LargeUpperPeakFreeform180', 'LargeUpperPeakFreeform270',
  'PeakLargeOffset', 'PeakLargeOffsetFlipped', 'PeakLargeOffsetFreeform', 'PeakLargeOffsetFlippedFreeform',
])

/** Is this shape ID (or prefix) any peak tile — old or new name? */
export const isPeakTile = (id: string | undefined | null): boolean =>
  typeof id === 'string' && PEAK_IDS.has(id)

/** Is this the upper/offset peak variant (fold near top) — old or new name? */
export const isUpperPeak = (id: string | undefined | null): boolean =>
  typeof id === 'string' && UPPER_PEAK_IDS.has(id)

/**
 * Legacy peak id/prefix → current canonical id/prefix. Covers square, freeform,
 * and flipped variants. Used to resolve OLD saved designs (whose shapes still
 * carry the pre-rename ids) against the current shape configs.
 *
 * The Large Upper Peak "Flipped" variant (a 180° tile) was replaced by the
 * four-way rotation set. Both its pre-rename name (PeakLargeOffsetFlipped) and
 * its post-rename name (LargeUpperPeakFlipped) now resolve to the 180° child, so
 * designs saved under either spelling still find a matching shape config.
 */
const LEGACY_ID_TO_CANONICAL: Record<string, string> = {
  PeakSmall: 'SmallPeak',
  PeakLarge: 'LargeCenterPeak',
  PeakLargeOffset: 'LargeUpperPeak',
  PeakLargeOffsetFlipped: 'LargeUpperPeak180',
  LargeUpperPeakFlipped: 'LargeUpperPeak180',
  PeakSmallFreeform: 'SmallPeakFreeform',
  PeakLargeFreeform: 'LargeCenterPeakFreeform',
  PeakLargeOffsetFreeform: 'LargeUpperPeakFreeform',
  PeakLargeOffsetFlippedFreeform: 'LargeUpperPeakFreeform180',
  LargeUpperPeakFlippedFreeform: 'LargeUpperPeakFreeform180',
}

/** Map a legacy peak id/prefix to its current name; returns the input unchanged if not legacy. */
export const canonicalizePeakId = (id: string | undefined | null): string | undefined | null =>
  (typeof id === 'string' && LEGACY_ID_TO_CANONICAL[id]) || id
